/* eslint-disable @typescript-eslint/no-require-imports */
import React, { useState } from 'react';
import {
  ImageBackground,
  ImageSourcePropType,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import { isIOS } from 'shared/constants/platform';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

export interface MapsCardProps extends PressableAnalyticsProps {
  id: string;
  imageSource?: ImageSourcePropType;
  onPress: () => void;
}

const getMapImageSource = (): ImageSourcePropType => {
  return isIOS
    ? (require('assets/images/rewards/apple-map.png') as ImageSourcePropType)
    : (require('assets/images/rewards/android-web-map.png') as ImageSourcePropType);
};

const MapsCard = ({
  id,
  imageSource,
  onPress,
  analyticsId = '',
  customAttributes = null,
}: MapsCardProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const cardPressAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });

  const handlePress = () => {
    cardPressAnalytics();
    onPress();
  };

  return (
    <Pressable
      style={[
        styles.mapCardContainer,
        isFocused ? styles.focusedStyle : null,
        isHovered ? styles.hoveredStyle : null,
        isPressed ? styles.pressedStyle : null,
      ]}
      onPress={handlePress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      role="button"
      testID={'map-card-' + id}
    >
      <View style={styles.imageWrapper}>
        <ImageBackground
          style={styles.imageBackground}
          source={imageSource ?? getMapImageSource()}
          role="img"
          accessible
        >
          <Pressable style={StyleSheet.absoluteFill} onPress={handlePress} />

          {isHovered ? (
            <View
              testID="map-hover-overlay"
              style={[styles.overlay, styles.hoveredStyle]}
            />
          ) : null}
          {isFocused ? (
            <View
              testID="map-focus-overlay"
              style={[styles.overlay, styles.focusedStyle]}
            />
          ) : null}
          {isPressed ? (
            <View
              testID="map-press-overlay"
              style={[styles.overlay, styles.pressedStyle]}
            />
          ) : null}
        </ImageBackground>
      </View>
    </Pressable>
  );
};

export const styles = StyleSheet.create({
  focusedStyle: {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderColor: theme.color.border.focused,
    borderRadius: 4,
  },
  hoveredStyle: {
    backgroundColor: 'rgba(28, 28, 28, 0.2)',
    borderRadius: 4,
  },
  imageBackground: {
    flex: 1,
    aspectRatio: 2.75,
    height: '100%',
    width: '100%',
  },
  imageWrapper: {
    borderColor: theme.color.border.primary,
    borderRadius: 4,
    borderWidth: 1,
    overflow: 'hidden',
  },
  mapCardContainer: {
    borderRadius: 4,
    margin: theme.spacing.m,
  },
  overlay: {
    ...StyleSheet.absoluteFill,
  },
  pressedStyle: {
    backgroundColor: 'rgba(28, 28, 28, 0.3)',
    borderRadius: 4,
  },
});

export default MapsCard;
